import React from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
  Typography,
  IconButton
} from '@mui/material';
import {
  MenuBook,
  Security,
  Person,
  Logout,
  Close,
  AccountBalance
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

interface MenuDrawerProps {
  open: boolean;
  onClose: () => void;
  counsellorId?: string;
}

const MenuDrawer: React.FC<MenuDrawerProps> = ({ open, onClose, counsellorId }) => {
  const navigate = useNavigate();
  const { logout, user } = useAuth();

  const handleNavigation = (path: string) => {
    navigate(path);
    onClose();
  };

  const handleLogout = async () => {
    try {
      await logout();
      onClose();
    } catch (err) {
      console.error('Error logging out:', err);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '85%', sm: 320 },
          background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
          borderLeft: '1px solid rgba(255, 255, 255, 0.1)'
        }
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box
          sx={{
            p: 3,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
              Menu
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
              {user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : 'Counsellor'}
            </Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ color: 'white' }}>
            <Close />
          </IconButton>
        </Box>

        {/* Menu Items */}
        <List sx={{ flex: 1, pt: 2 }}>
          <ListItem
            button
            onClick={() => handleNavigation(`/counsellors/${counsellorId}`)}
            disabled={!counsellorId}
            sx={{
              py: 2,
              '&:hover': {
                backgroundColor: 'rgba(102, 126, 234, 0.1)'
              }
            }}
          >
            <ListItemIcon>
              <Person sx={{ color: '#667eea' }} />
            </ListItemIcon>
            <ListItemText
              primary="View / Edit Profile"
              primaryTypographyProps={{
                sx: { color: 'rgba(255, 255, 255, 0.9)', fontWeight: 500 }
              }}
            />
          </ListItem>

          <ListItem
            button
            onClick={() => handleNavigation('/counsellor/policies')}
            sx={{
              py: 2,
              '&:hover': {
                backgroundColor: 'rgba(245, 158, 11, 0.1)'
              }
            }}
          >
            <ListItemIcon>
              <MenuBook sx={{ color: '#f59e0b' }} />
            </ListItemIcon>
            <ListItemText
              primary="Policies & Procedures"
              primaryTypographyProps={{
                sx: { color: 'rgba(255, 255, 255, 0.9)', fontWeight: 500 }
              }}
            />
          </ListItem>

          <ListItem
            button
            onClick={() => handleNavigation('/mtd')}
            sx={{
              py: 2,
              '&:hover': {
                backgroundColor: 'rgba(198, 40, 40, 0.1)'
              }
            }}
          >
            <ListItemIcon>
              <AccountBalance sx={{ color: '#c62828' }} />
            </ListItemIcon>
            <ListItemText
              primary="Tax Digital"
              primaryTypographyProps={{
                sx: { color: 'rgba(255, 255, 255, 0.9)', fontWeight: 500 }
              }}
            />
          </ListItem>

          <ListItem
            button
            onClick={() => handleNavigation('/settings')}
            sx={{
              py: 2,
              '&:hover': {
                backgroundColor: 'rgba(16, 185, 129, 0.1)'
              }
            }}
          >
            <ListItemIcon>
              <Security sx={{ color: '#10b981' }} />
            </ListItemIcon>
            <ListItemText
              primary="Security Settings"
              primaryTypographyProps={{
                sx: { color: 'rgba(255, 255, 255, 0.9)', fontWeight: 500 }
              }}
            />
          </ListItem>

          <Divider sx={{ my: 2, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

          <ListItem
            button
            onClick={handleLogout}
            sx={{
              py: 2,
              '&:hover': {
                backgroundColor: 'rgba(244, 67, 54, 0.1)'
              }
            }}
          >
            <ListItemIcon>
              <Logout sx={{ color: '#f44336' }} />
            </ListItemIcon>
            <ListItemText
              primary="Logout"
              primaryTypographyProps={{
                sx: { color: 'rgba(255, 255, 255, 0.9)', fontWeight: 500 }
              }}
            />
          </ListItem>
        </List>

        {/* Footer */}
        <Box
          sx={{
            p: 2,
            borderTop: '1px solid rgba(255, 255, 255, 0.1)',
            textAlign: 'center'
          }}
        >
          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
            Evolving Minds CRM
          </Typography>
        </Box>
      </Box>
    </Drawer>
  );
};

export default MenuDrawer;
